import { router } from 'expo-router'
import { useCallback, type ReactNode } from 'react'
import { ScrollView, View } from 'react-native'

import { Avatar } from '@/src/elements/avatar'
import { Button } from '@/src/elements/button/default'
import { PrayButton } from '@/src/elements/button/pray/PrayButton'
import { Divider } from '@/src/elements/divider/Divider'
import { Header } from '@/src/elements/header'
import { Message } from '@/src/elements/message'
import { Spacer } from '@/src/elements/spacer/Spacer'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { ErrorScreen } from '@/src/general/ui/screens/ErrorScreen'
import { getAvatarUrl } from '@/src/general/utils/getAvatarUrl'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants/TrackingSource.enum'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { INTENTION_TYPE } from '../../../models/Intention.enums'
import { useIntentionQuery } from '../../../services/useIntentionQuery'
import { IntentionCard } from '../../components/IntentionCard'
import { IntentionShareModal } from '../../components/IntentionShareModal'
import { useMarkIntentionAsShared } from '../../hooks/useMarkIntentionAsShared'
import { IntentionPlaceholder } from './components/IntentionPlaceholder'
import { IntentionSinglePrayers } from './IntentionSinglePrayers'

type TIntentionSingleScreen = {
  intentionId: number
  shareToken: string
}

export const IntentionSingleScreen = ({ intentionId, shareToken }: TIntentionSingleScreen): ReactNode => {
  const { t } = useTranslate()
  const { routes } = useRoutes()

  const { data: me } = useMeQuery()

  const { intention, error, isLoading } = useIntentionQuery(intentionId, shareToken, INTENTION_TYPE.INTENTION)

  useMarkIntentionAsShared({ isIntentionShared: isLoading ? undefined : !!intention?.shared, intentionId })

  const handlePray = () => {
    router.navigate(
      routes.intentionsPray({
        id: intentionId,
        type: INTENTION_TYPE.INTENTION,
        shareToken,
        source: TRACKING_SOURCE.INTENTION_PAGE,
        theme: undefined,
      }),
    )
  }

  const navigateOnIntentions = useCallback(() => {
    router.navigate(routes.intentions())
  }, [routes])

  if (error || (!intention && !isLoading))
    return (
      <ErrorScreen
        error={error}
        fallbackRoute={routes.intentions()}
        defaultErrorKey="intention.load.error"
        testID={TEST_ID.INTENTION.SCREEN.ERROR}
      />
    )

  return (
    <>
      <ScrollView testID={TEST_ID.INTENTION.SCREEN.NAME} contentContainerStyle={{ minHeight: '100%' }}>
        <IntentionShareModal
          trackingSource={TRACKING_SOURCE.INTENTION_PAGE}
          intention={intention}
          intentionId={intentionId}
          shareToken={shareToken}
        >
          {({ isLoading: isShareLoading, onShare }) => (
            <Header
              leftButtons={[{ startIcon: ICON.ARROW_LEFT, onPress: navigateOnIntentions, type: 'icon' }]}
              rightButtons={[
                {
                  type: 'icon',
                  startIcon: ICON.SHARE,
                  testID: ICON.SHARE,
                  onPress: onShare,
                  isDisabled: isShareLoading,
                },
              ]}
            />
          )}
        </IntentionShareModal>
        {!!isLoading && <IntentionPlaceholder testID={TEST_ID.INTENTIONS.CARD.LOADING} />}
        {/* Content */}
        {!isLoading && (
          <View className="flex-column native:mx-6 mx-6 mb-10 flex max-w-[400px] flex-1 gap-6 web:h-full web:justify-center web:self-center ">
            {/* Submitter infos  */}
            <View className="flex-col items-center gap-2">
              {intention != null && (
                <>
                  <Avatar src={getAvatarUrl(intention.user?.avatar)} size="md" />
                  <Typography className="max-w-[283px] text-center" testID={TEST_ID.MISCELLANEOUS.INTENTION_SUBMITTER}>
                    {t('intention.invitation-to-pray-for', { name: intention?.user?.name })}
                  </Typography>
                </>
              )}
            </View>

            <IntentionCard color="default" type={INTENTION_TYPE.INTENTION} intention={intention}>
              {intention?.hasPrayed && (
                <Message iconName={ICON.CHECK_CIRCLE_BROKEN} colors="success" className="text-mdlc">
                  {t('intention.prayed.already-with', { count: intention.prayersCount })}
                </Message>
              )}
              {intention && !intention.hasPrayed && (
                <PrayButton
                  testID={TEST_ID.INTENTION.PRAY.SCREEN.BUTTON}
                  size="lg"
                  onPress={handlePray}
                  isDisabled={!intention || me?.id === intention.user?.id}
                  fullWidth
                  label={t('pray.i-pray.short')}
                />
              )}
            </IntentionCard>

            {intention && (
              <View className="flex-col items-center gap-2">
                {!intention.hasPrayed && (
                  <IntentionSinglePrayers prayers={intention.prayers} prayersCount={intention.prayersCount ?? 0} />
                )}
                {intention.hasPrayed && (
                  <View className="flex h-full flex-col ">
                    <Divider orientation="horizontal" />
                    <Spacer y={4} />
                    <Typography bold className="text-lg">
                      {t('intention.prayed.pray-for-others')}
                    </Typography>
                    <Spacer y={4} />
                    <Button
                      testID={TEST_ID.INTENTION.VIEW_ALL}
                      onPress={navigateOnIntentions}
                      colors="primary"
                      fullWidth
                      variant="solid"
                      startIcon={ICON.INTENTION}
                    >
                      {t('intentions.view')}
                    </Button>
                  </View>
                )}
              </View>
            )}
          </View>
        )}
      </ScrollView>
    </>
  )
}
